import { type ReportDesignOwner } from '@store/api/reportDesignEndpoints';
import { useGetTeamDirectoryQuery } from '@store/api/teamEndpoints';
import { usePermission } from '@store/hooks';
import { type RootState } from '@store/store';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

/**
 * Where the reader may create a design: what a new one, or a copy, can be given as its owner.
 *
 * A global author may create one for the organization and in any team, and typically
 * belongs to none, so their own memberships cannot produce the list: they get the whole
 * directory. Everyone else gets the teams their own role grants it in.
 *
 * `ready` is false while a global author's directory is still being read. Until it has
 * answered their list is the organization alone, and a design started or copied then would
 * be the organization's for want of anything else: starting and copying wait for it.
 *
 * With `skip` nothing is asked for and nothing is offered: for a page that turns out not to
 * need the answer.
 */
export const useDesignOwners = ({ skip = false } = {}): { owners: ReportDesignOwner[]; ready: boolean } => {
  const { can } = usePermission();
  const sessionTeams = useSelector((state: RootState) => state.auth.user?.teams);

  const canCreateAnywhere = can('report_design:post');
  const { data: teamDirectory, isLoading: isLoadingDirectory } = useGetTeamDirectoryQuery(undefined, {
    skip: skip || !canCreateAnywhere,
  });

  const owners = useMemo<ReportDesignOwner[]>(
    () => {
      if (skip) return [];

      return canCreateAnywhere
        ? [
          { teamId: null, teamName: null },
          ...(teamDirectory ?? []).map((team) => ({ teamId: team._id, teamName: team.name })),
        ]
        : (sessionTeams ?? [])
          .filter((team) => can('report_design:post', team.id))
          .map((team) => ({ teamId: team.id, teamName: team.name }));
    },
    [skip, canCreateAnywhere, teamDirectory, sessionTeams, can]
  );

  return { owners, ready: !skip && (!canCreateAnywhere || !isLoadingDirectory) };
};
